<template>
  <div class="manager-layout">
    <header class="manager-header">
      <div class="header-content">
        <div class="header-left">
          <h1 class="logo">⚙️ オトエイゴ 管理者</h1>
          <nav class="manager-nav">
            <router-link to="/manager" class="nav-link" active-class="active">ダッシュボード</router-link>
            <router-link to="/manager/instructors" class="nav-link" active-class="active">講師管理</router-link>
            <router-link to="/manager/submissions" class="nav-link" active-class="active">提出管理</router-link>
            <router-link to="/manager/feedback-playbacks" class="nav-link" active-class="active">FB再生履歴</router-link>
            <router-link to="/manager/trial-progress" class="nav-link" active-class="active">体験期間進捗</router-link>
            <router-link to="/manager/users" class="nav-link" active-class="active">ユーザー一覧</router-link>
            <router-link to="/manager/onboarding-progress" class="nav-link" active-class="active">登録ステップ状況</router-link>
            <router-link to="/manager/line-funnel" class="nav-link" active-class="active">LINEファネル進捗</router-link>
            <router-link to="/manager/shikoku" class="nav-link" active-class="active">四国 追加者一覧</router-link>
            <router-link to="/manager/processing" class="nav-link" active-class="active">処理・キュー</router-link>
            <router-link to="/manager/billing" class="nav-link" active-class="active">サブスク・支払い</router-link>
            <router-link to="/manager/learners" class="nav-link" active-class="active">学習者一覧</router-link>
            <router-link to="/manager/canceled-learners" class="nav-link" active-class="active">解約ユーザー</router-link>
            <router-link to="/manager/individual-analysis" class="nav-link" active-class="active">個別分析送付</router-link>
          </nav>
        </div>
        <div class="header-right">
          <div class="user-info">
            <span class="user-display">{{ headerUserName }}</span>
            <button
              v-if="pwaState.canSubscribe"
              type="button"
              class="btn-pwa"
              :disabled="pwaState.enabled"
              @click="subscribeManagerPush"
              title="1時間ごとの管理レポートをPWA通知します"
            >
              {{ pwaState.enabled ? '通知有効' : 'PWA通知を有効化' }}
            </button>
          </div>
          <div v-if="pwaState.message" class="pwa-msg" :class="{ ok: pwaState.enabled }">{{ pwaState.message }}</div>
        </div>
      </div>
    </header>

    <main class="manager-main">
      <slot />
    </main>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { supabase } from '../../supabaseClient'

const router = useRouter()
const headerUserName = ref('')

const pwaState = ref({
  canSubscribe: false,
  enabled: false,
  message: ''
})

const getToken = async () => {
  const { data: { session } } = await supabase.auth.getSession()
  return session?.access_token || null
}

const urlBase64ToUint8Array = (base64String) => {
  // VAPID を URL-safe base64 -> Uint8Array に変換
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/')

  const rawData = atob(base64)
  const outputArray = new Uint8Array(rawData.length)
  for (let i = 0; i < rawData.length; ++i) outputArray[i] = rawData.charCodeAt(i)
  return outputArray
}

const apiBase = import.meta.env.VITE_API_BASE_URL || ''

/** SW＋Push の既存購読があれば UI を「通知有効」に同期（再ログイン後も毎回押す必要をなくす） */
const syncPwaButtonFromSavedSubscription = async () => {
  try {
    if (!('Notification' in window) || !('serviceWorker' in navigator) || !('PushManager' in window)) return
    if (Notification.permission !== 'granted') return

    const regs = await navigator.serviceWorker.getRegistrations()
    const managerReg = regs.find((r) => {
      const u = r.active?.scriptURL || r.installing?.scriptURL || r.waiting?.scriptURL || ''
      return u.includes('/manager/sw.js')
    })
    if (!managerReg) return

    const sub = await managerReg.pushManager.getSubscription()
    if (sub) {
      pwaState.value.enabled = true
      pwaState.value.message = 'PWA通知は有効です'
    }
  } catch (e) {
    console.error('syncPwaButtonFromSavedSubscription error:', e)
  }
}

const subscribeManagerPush = async () => {
  try {
    if (!('Notification' in window) || !('serviceWorker' in navigator) || !('PushManager' in window)) {
      pwaState.value.message = 'この環境では Push 通知に対応していません'
      return
    }

    if (Notification.permission === 'default') {
      const result = await Notification.requestPermission()
      if (result !== 'granted') {
        pwaState.value.message = '通知の許可が必要です'
        return
      }
    } else if (Notification.permission !== 'granted') {
      pwaState.value.message = '通知の許可が未設定です'
      return
    }

    // manager 用 SW は /manager/ 配下に置き、scope も /manager/ に固定して
    // 通常アプリ（/）と PWA として分離できるようにする
    const swUrl = '/manager/sw.js'
    const registration = await navigator.serviceWorker.register(swUrl)
    const existingSub = await registration.pushManager.getSubscription()

    const publicKeyB64 = import.meta.env.VITE_PWA_VAPID_PUBLIC_KEY || ''
    if (!publicKeyB64) {
      pwaState.value.message = 'VAPID 公開鍵が未設定です（VITE_PWA_VAPID_PUBLIC_KEY）'
      return
    }

    let subscription = existingSub
    if (!subscription) {
      const convertedVapidKey = urlBase64ToUint8Array(publicKeyB64)
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: convertedVapidKey
      })
    }

    const token = await getToken()
    if (!token) {
      pwaState.value.message = '認証に失敗しました（ログインし直してください）'
      return
    }

    const subJson = subscription.toJSON()
    const res = await fetch(`${apiBase}/api/pwa/manager-subscribe`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({
        endpoint: subJson.endpoint,
        keys: {
          p256dh: subJson.keys?.p256dh,
          auth: subJson.keys?.auth
        }
      })
    })

    if (!res.ok) {
      pwaState.value.message = '購読登録に失敗しました'
      return
    }

    pwaState.value.enabled = true
    pwaState.value.message = 'PWA通知を有効化しました'
  } catch (e) {
    console.error('subscribeManagerPush error:', e)
    pwaState.value.message = '通知有効化に失敗しました'
  }
}

onMounted(async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) {
      router.push('/')
      return
    }
    const { data: profile } = await supabase
      .from('users_phase2')
      .select('full_name, email, role')
      .eq('id', user.id)
      .single()
    const name = (profile?.full_name || '').trim()
    headerUserName.value = name || profile?.email || user.email || '管理者'

    pwaState.value.canSubscribe = profile?.role === 'manager'
    if (pwaState.value.canSubscribe) await syncPwaButtonFromSavedSubscription()
  } catch (error) {
    console.error('ユーザー情報取得エラー:', error)
    router.push('/')
  }
})
</script>

<style scoped>
.manager-layout {
  min-height: 100vh;
  background: #f1f5f9;
  /* OS/ブラウザの配色設定に依存せず、管理画面は常に可読な明配色に固定 */
  color-scheme: light;
  color: #0f172a;
}

.manager-header {
  background: #1e293b;
  color: #f8fafc;
  border-bottom: 2px solid #334155;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  position: sticky;
  top: 0;
  z-index: 100;
}

.header-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 1.5rem;
}

.header-left .logo {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 700;
}

.manager-nav {
  display: flex;
  gap: 0.5rem;
}

.manager-nav .nav-link {
  color: #94a3b8;
  text-decoration: none;
  padding: 0.35rem 0.75rem;
  border-radius: 6px;
  font-size: 0.9rem;
}

.manager-nav .nav-link:hover {
  color: #f1f5f9;
}

.manager-nav .nav-link.active {
  color: #f1f5f9;
  background: rgba(255, 255, 255, 0.1);
}

.header-right .user-info {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.user-display {
  font-size: 0.9rem;
  font-weight: 500;
  color: #cbd5e1;
}

.btn-pwa {
  padding: 0.35rem 0.6rem;
  border-radius: 6px;
  font-size: 0.8rem;
  cursor: pointer;
  border: 1px solid rgba(255, 255, 255, 0.25);
  color: #f8fafc;
  background: rgba(255, 255, 255, 0.08);
}

.btn-pwa:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.pwa-msg {
  margin-top: 0.35rem;
  font-size: 0.78rem;
  color: #cbd5e1;
}

.pwa-msg.ok {
  color: #86efac;
}

.manager-main {
  max-width: 1200px;
  margin: 0 auto;
  padding: 1.5rem;
  color: #0f172a;
}

@media (max-width: 768px) {
  .header-content {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }
  .header-left .logo {
    font-size: 1.1rem;
  }
}
</style>
